import { ZProgress } from "@/shared/components/progress";
import { cn } from "@/shared/utils/classname-merger";
import { Spinner, Tooltip } from "@nextui-org/react";
import { FileIcon, XIcon } from "lucide-react";
import React from "react";

interface FileDisplayProps {
  fileName: string;
  status?: "success" | "failed" | "uploading" | "pending" | "gettingurl" | "inprogress" | "error";
  showRemoveButton?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  className?: string;
  size?: "sm" | "md";
  progress?: number;
  errorMessage?: string;
}

const FileDisplay: React.FC<FileDisplayProps> = ({
  fileName,
  status = "success",
  showRemoveButton = false,
  onRemove,
  onClick,
  className,
  size = "md",
  progress,
  errorMessage
}) => {
  const displayName = fileName.length > 20 ? fileName.slice(0, 20) + "..." : fileName;
  
  const getStatusColor = () => {
    switch (status) {
      case "success":
        return "text-green-600";
      case "failed":
      case "error":
        return "text-red-600";
      case "uploading":
      case "inprogress":
      case "pending":
      case "gettingurl":
        return "text-orange-600";
      default:
        return "text-gray-600";
    }
  };

  const getIconColor = () => {
    switch (status) {
      case "success":
        return "text-green-500";
      case "failed":
      case "error":
        return "text-red-500";
      case "uploading":
      case "inprogress":
      case "pending":
      case "gettingurl":
        return "text-orange-500";
      default:
        return "text-gray-500";
    }
  };

  const getBorderColor = () => {
    switch (status) {
      case "error":
      case "failed":
        return "border-red-500";
      default:
        return "border-gray-200";
    }
  };

  const shouldShowProgress = () => {
    return (status === "inprogress" || status === "uploading") && progress !== undefined && progress >= 0;
  };

  const sizeClasses = size === "sm" 
    ? "p-1 text-xs" 
    : "p-2 text-sm";

  const fileChip = (
    <div 
      className={cn(
        "relative flex items-center justify-between border bg-white rounded-md cursor-pointer hover:bg-gray-50",
        getBorderColor(),
        sizeClasses,
        className
      )}
      onClick={onClick}
      title={errorMessage || ""}
    >
      <FileIcon className={cn("w-4 h-4 mr-2 flex-shrink-0", getIconColor())} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={cn("leading-4 whitespace-nowrap truncate", getStatusColor())}>
            {displayName}
          </p>
          {status === "gettingurl" && <Spinner size="sm" />}
        </div>
        
        {/* Progress bar for uploading files */}
        {shouldShowProgress() && (
          <ZProgress
            aria-label="file-upload-progress"
            value={progress || 0}
            maxValue={100}
            className="h-[2px] mt-1"
          />
        )}
      </div>
      
      {showRemoveButton && onRemove && (
        <button
          className="absolute -top-1 -right-1 bg-gray-200 text-gray-600 hover:bg-gray-400 hover:text-white w-4 h-4 flex items-center justify-center rounded-full text-xs z-10"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <XIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Wrap with tooltip if there's an error message
  if (status === "error" && errorMessage) {
    return (
      <Tooltip
        content={errorMessage}
        placement="top"
        showArrow
        classNames={{
          content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
          arrow: "backdrop-blur-md",
          base: "before:bg-black/95",
        }}
      >
        {fileChip}
      </Tooltip>
    );
  }

  return fileChip;
};

export default FileDisplay;